// Pipeline stage model + transition rules. See docs/huntrack-target-workflow.md §3.
// Backward-compatible: legacy stage labels are mapped to canonical stages, and
// transitions from unmapped/unknown legacy values are allowed (lenient) so
// existing deals are never blocked.

export const CANONICAL_STAGES = [
  'New',
  'Attempting Contact',
  'Connected',
  'Qualified',
  'Meeting Scheduled',
  'Sales Handover',
  'Nurture',
  'Closed - Won',
  'Closed - Lost',
  'Disqualified',
]

// Main forward progression (excludes Nurture + terminal branches).
const ORDER = ['New', 'Attempting Contact', 'Connected', 'Qualified', 'Meeting Scheduled', 'Sales Handover', 'Closed - Won']

const TERMINAL = new Set(['Closed - Won', 'Closed - Lost', 'Disqualified'])
const BRANCH = new Set(['Nurture', 'Closed - Lost', 'Disqualified']) // reachable from any non-terminal

// Legacy labels (existing pipeline/lead_status data) -> canonical.
export const LEGACY_STAGE_MAP = {
  'Engaged': 'Connected',
  'Contacted': 'Connected',
  'Demo Scheduled': 'Meeting Scheduled',
  'Proposal Sent': 'Sales Handover',
  'Negotiation': 'Sales Handover',
  'Follow Up Later': 'Nurture',
  'Not Interested': 'Closed - Lost',
  'Closed Won': 'Closed - Won',
  'Closed Lost': 'Closed - Lost',
  // already canonical values map to themselves implicitly
}

export function canonical(stage) {
  if (!stage) return null
  if (CANONICAL_STAGES.includes(stage)) return stage
  return LEGACY_STAGE_MAP[stage] || null // null = unknown/legacy-unmapped
}

export function isTerminal(stage) {
  return TERMINAL.has(canonical(stage))
}

// Reason required when closing lost, disqualifying, moving to nurture, or
// reopening a terminal deal.
export function requiresReason(from, to) {
  const tc = canonical(to)
  if (tc === 'Nurture' || tc === 'Closed - Lost' || tc === 'Disqualified') return true
  if (isTerminal(from) && !isTerminal(to)) return true // reopen
  return false
}

// Is a stage change allowed?
export function canTransition(from, to) {
  const fc = canonical(from)
  const tc = canonical(to)
  if (!tc) return false              // target must be a known stage
  if (fc === tc) return false        // no-op
  if (!fc) return true               // legacy/unknown source: be lenient

  // To a branch stage (Nurture / Closed-Lost / Disqualified): allowed from any non-terminal
  if (BRANCH.has(tc)) return !TERMINAL.has(fc)

  // Reopen from terminal -> only back to Attempting Contact or Nurture (Nurture handled above)
  if (TERMINAL.has(fc)) return tc === 'Attempting Contact'

  // Nurture -> re-engage
  if (fc === 'Nurture') return tc === 'Attempting Contact'

  // Main order: allow de-escalation (backwards) or one step forward (no skipping)
  const iF = ORDER.indexOf(fc)
  const iT = ORDER.indexOf(tc)
  if (iF === -1 || iT === -1) return false
  return iT <= iF || iT === iF + 1
}

// Options to show in a stage <select> for a deal whose current value may be legacy.
export function stageOptions(currentStage) {
  const opts = [...CANONICAL_STAGES]
  if (currentStage && !opts.includes(currentStage)) opts.unshift(currentStage) // keep legacy value selectable
  return opts
}
